import { createFileRoute } from '@tanstack/react-router';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, toast } from '@ludolio/shared-ui';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api';
import { EmailNotificationSettings } from '@/components/EmailNotificationSettings';

export const Route = createFileRoute('/_authenticated/account')({
  component: AccountPage,
});

function AccountPage() {
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
  }, [user?.firstName, user?.lastName]);

  const canSave = useMemo(() => {
    if (!user) return false;
    return (
      firstName.trim() !== (user.firstName ?? '') ||
      lastName.trim() !== (user.lastName ?? '')
    );
  }, [firstName, lastName, user]);

  const onCancel = () => {
    if (!user) return;
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user || saving || !canSave) return;

    setSaving(true);
    try {
      const res = await apiClient.updateName(firstName.trim(), lastName.trim());
      if (!res?.success) {
        throw new Error(res?.error || res?.message || 'Failed to update account');
      }

      const updatedUser: any = (res as any)?.data?.user ?? (res as any)?.data;
      if (updatedUser) {
        setAuth({
          ...user,
          ...updatedUser,
          needsOnboarding: updatedUser.needsOnboarding ?? user.needsOnboarding,
          needsOrgNoticeConsent: updatedUser.needsOrgNoticeConsent ?? user.needsOrgNoticeConsent,
        });
      }

      toast.success('Account updated');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Loading account…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <Card>
        <CardHeader>
          <CardTitle as="h1">Account</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={onSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" value={user.email ?? ''} readOnly disabled />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !canSave}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <EmailNotificationSettings />
    </div>
  );
}

